/*
* Turning billing numbers into the words a customer reads.
*
* The API sends days and cents because those are what it can do arithmetic on.
* These render them - "3 months", "$1,000" - without any screen deciding for
* itself what the offer is.
*/

/*
* A duration in days as the customer would say it. 90 days is "3 months", not
* "90 days"; anything that is not close to a whole number of months stays in
* days rather than being rounded into a lie.
*/
export const describeDuration = (days: number): string => {
    if (!days) {
        return '0 days';
    }

    if (days % 7 === 0 && days < 28) {
        const weeks = days / 7;
        return `${weeks} week${weeks === 1 ? '' : 's'}`;
    }

    const months = Math.round(days / 30);
    if (months >= 1 && Math.abs(days - months * 30) <= 3) {
        return `${months} month${months === 1 ? '' : 's'}`;
    }

    return `${days} day${days === 1 ? '' : 's'}`;
};

/* Cents to money. Whole amounts lose the trailing zeros - $1,000, not $1,000.00. */
export const formatMoney = (amountCents: number, currency: string = 'usd'): string => {
    const amount = (amountCents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

/* Money in, cents out. Accepts "1,000", "$1,000" and "1000.50" alike. */
export const parseMoneyToCents = (value: string): number | null => {
    const cleaned = (value || '').replace(/[$,\s]/g, '');
    if (cleaned === '') {
        return null;
    }
    const amount = Number(cleaned);
    if (Number.isNaN(amount) || amount < 0) {
        return null;
    }
    return Math.round(amount * 100);
};

/* A trial end date as a short, unambiguous day. */
export const formatDate = (isoDate: string | null): string => {
    if (!isoDate) {
        return '-';
    }
    const parsed = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return isoDate;
    }
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/* How much time is left, said the way someone would say it out loud. */
export const describeRemaining = (daysRemaining: number | null): string => {
    if (daysRemaining === null) {
        return 'No end date';
    }
    if (daysRemaining === 0) {
        return 'Ends today';
    }
    return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`;
};
